import { Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, type GridColDef } from '@mui/x-data-grid';
import React, { useState, type Dispatch, type SetStateAction } from 'react';
import { useAlert } from '../../context/AlertContext';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import {
  useCreateFromArnoTopNoSync,
  useGetARNOtopNoSync,
} from '../../hooks/reactQuerys/ARNOtopQueries';

interface IProps {
  legalClientArray: ILegalClient[];
  setLegalClientArray: Dispatch<SetStateAction<ILegalClient[]>>;
  onSave: () => void;
  onClose: () => void;
}

const columns: GridColDef[] = [
  { field: 'idArnoTop', headerName: 'Namen-Nr.', width: 100 },
  {
    field: 'idLegalClient',
    headerName: 'synchronisiert',
    width: 120,
    renderCell: params => (params.value !== null ? <Check /> : <Close />),
  },
  { field: 'FirstName', headerName: 'Vorname', width: 170 },
  { field: 'LastName', headerName: 'Nachname', width: 170 },
  { field: 'Email', headerName: 'E-Mail-Adresse', flex: 1 },
];

export const AddFromArnotopDialog: React.FC<IProps> = ({ onSave, onClose }) => {
  const {
    arnoTopNoSyncArray,
    isLoadingArnoTopNoSyncArray,
    refetchArnoTopNoSyncArray,
  } = useGetARNOtopNoSync();

  const { createFromArnoTopNoSync, isPendingCreateFromArnoTopNoSync } =
    useCreateFromArnoTopNoSync();

  const [selectedIdArnoTop, setSelectedIdArnoTop] = useState<number | null>(
    null
  );

  const { showAlert } = useAlert();

  const syncedArnoTop =
    arnoTopNoSyncArray?.filter(x => x.idLegalClient !== null) ?? [];
  const noSyncedArnoTop =
    arnoTopNoSyncArray?.filter(x => x.idLegalClient === null) ?? [];
  const sortedArnoTopArray = [...noSyncedArnoTop, ...syncedArnoTop];

  const handleSave = async () => {
    if (selectedIdArnoTop) {
      try {
        await createFromArnoTopNoSync(selectedIdArnoTop);
        showAlert({ text: 'Mandant erfolgreich importiert' });
        setSelectedIdArnoTop(null);
        onSave();
        refetchArnoTopNoSyncArray();
      } catch {
        showAlert({
          text: 'Fehler beim Importieren des Mandanten',
          severity: 'error',
        });
      }
    }
  };

  if (isLoadingArnoTopNoSyncArray) {
    return (
      <Dialog open onClose={onClose} maxWidth='lg' fullWidth scroll='paper'>
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (!arnoTopNoSyncArray) {
    return (
      <Dialog open onClose={onClose} maxWidth='lg' fullWidth scroll='paper'>
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography variant='h6'>
            Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.
          </Typography>
          <Button
            variant='contained'
            onClick={() => refetchArnoTopNoSyncArray()}
          >
            Erneut versuchen
          </Button>
        </Box>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
        {arnoTopNoSyncArray === undefined && (
          <LinearProgress
            sx={{
              height: '4px',
            }}
          />
        )}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Typography variant='h5'>Neue Namen aus ARNOtop</Typography>
        </Box>

        {arnoTopNoSyncArray === undefined ? (
          <DialogContent sx={{ minHeight: '70vh' }}>
            <CircularProgress />
          </DialogContent>
        ) : (
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DataGrid
                  localeText={
                    deDE.components.MuiDataGrid.defaultProps.localeText
                  }
                  rows={sortedArnoTopArray}
                  columns={columns}
                  getRowId={row => row.idArnoTop}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 25,
                      },
                    },
                  }}
                  autoHeight
                  onRowClick={params => setSelectedIdArnoTop(Number(params.id))}
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button variant='outlined' onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant='contained'
            disabled={
              selectedIdArnoTop === null || isPendingCreateFromArnoTopNoSync
            }
            onClick={handleSave}
          >
            {isPendingCreateFromArnoTopNoSync ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Mandant importieren</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Mandant importieren'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
